import {
  CascoPackage,
  VehicleBrand,
  VehicleList
} from '@insurance/api/requests'
import {
  AddVehicleButton,
  BooleanRadioCardGroup,
  ColorAutoComplete,
  EditVehicleBrandSelect,
  EditVehicleModelSelect
} from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { InputParsedNumber, Text } from '@shared/components'
import { capitalize } from '@shared/utils'
import { Col, DatePicker, Flex, Form, Input, Row } from 'antd'
import dayjs from 'dayjs'
import { Add } from 'iconsax-react'
import { useTranslation } from 'react-i18next'

import { useCascoInsuranceValues } from './CascoInsuranceContext'
import {
  enableEngineFields,
  formProps,
  onlyVehicle,
  vehicleMaxYear
} from './utils'

import { VehicleCategorySelect } from './VehicleCategorySelect'
import { VehicleFuelSelect } from './VehicleFuelSelect'
import { VehicleTransmissionSelect } from './VehicleTransmissionSelect'

export const EditVehicle = () => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()
  const { isEditable, order } = useCascoInsuranceValues()
  const { required, beforeYear, vehicleRegistrationNumber } =
    useValidationsField()
  const {
    insurancePackageProps,
    vehicleBrandProps,
    vehicleModelProps,
    vehicleYearProps,
    vehicleVinProps,
    vehicleRegistrationNumberProps,
    vehicleColorProps,
    vehicleFuelProps,
    vehicleTransmissionProps,
    vehicleRootProps,
    vehicleCategoryProps,
    vehicleCertificateNumberProps,
    vehicleMileAgeProps,
    vehicleIsPremium,
    vehicleIsFromChina,
    vehicleSeatsProps,
    vehicleWeightProps,
    vehicleEngineCapacityProps,
    vehicleEngineNumberProps
  } = formProps(t)

  const vehicleValue = Form.useWatch(vehicleRootProps.name, { preserve: true })
  const insurancePackage = Form.useWatch<CascoPackage>(
    insurancePackageProps.name,
    { preserve: true }
  )

  const handleAddVehicle = (vehicle?: VehicleList) => {
    const formattedVehicle = {
      ...vehicleValue,
      ...vehicle,
      [vehicleYearProps.name[1]]: vehicle?.year
        ? dayjs(String(vehicle.year))
        : undefined,
      [vehicleColorProps.name[1]]: vehicle?.color
        ? capitalize(vehicle.color)
        : undefined
    }
    formInstance.setFieldValue(vehicleRootProps.name, formattedVehicle)
  }

  const handleBrandFound = (brand: VehicleBrand) => {
    const formattedVehicle = {
      ...vehicleValue,
      [vehicleIsPremium.name[1]]: brand.is_premium,
      ...(!order && {
        [vehicleIsFromChina.name[1]]: brand.is_china
      })
    }

    formInstance.setFieldValue(vehicleRootProps.name, formattedVehicle)
  }

  const showEngineFields = enableEngineFields(
    vehicleValue?.[vehicleCategoryProps.name[1]]
  )

  const minVehicleYear = vehicleMaxYear[insurancePackage]
  const isMini = insurancePackage === CascoPackage.MINI

  return (
    <Flex vertical gap={12}>
      <Flex
        align="center"
        gap={12}
        justify="space-between"
        style={{ paddingBottom: 12 }}
      >
        <Text size={16} strong>
          {t('vehicle')}
        </Text>

        <AddVehicleButton
          type="link"
          icon={<Add size={16} />}
          value={vehicleValue}
          multiple={false}
          onAdd={handleAddVehicle}
          disabled={isEditable}
        >
          {t('add_existing_vehicle')}
        </AddVehicleButton>
      </Flex>

      <Row gutter={[12, 12]}>
        <Col xs={24}>
          <Form.Item {...vehicleCategoryProps} rules={[required]}>
            <VehicleCategorySelect
              disabled={onlyVehicle(insurancePackage) || isEditable}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item {...vehicleBrandProps} rules={[required]}>
            <EditVehicleBrandSelect
              disabled={isEditable}
              vehicleValue={vehicleValue}
              placeholder={vehicleBrandProps?.label}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item {...vehicleModelProps} rules={[required]}>
            <EditVehicleModelSelect
              disabled={isEditable}
              onBrandFound={handleBrandFound}
              brand={vehicleValue?.brand?.toUpperCase()}
              placeholder={vehicleModelProps?.label}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            {...vehicleYearProps}
            rules={[required, beforeYear(minVehicleYear)]}
          >
            <DatePicker.YearPicker
              minDate={minVehicleYear}
              maxDate={dayjs()}
              placeholder={vehicleYearProps.label}
              className="w-full"
              disabled={isEditable}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            {...vehicleVinProps}
            normalize={(value: string) => value?.toUpperCase()}
            rules={[required]}
          >
            <Input placeholder={vehicleVinProps.label} disabled={isEditable} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            {...vehicleRegistrationNumberProps}
            normalize={(value: string) => value?.toUpperCase()}
            rules={[required, vehicleRegistrationNumber]}
          >
            <Input placeholder={vehicleRegistrationNumberProps.label} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item {...vehicleCertificateNumberProps} rules={[required]}>
            <Input placeholder={vehicleCertificateNumberProps.label} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item {...vehicleColorProps} rules={isMini ? [] : [required]}>
            <ColorAutoComplete
              disabled={isEditable}
              placeholder={vehicleColorProps.label}
            />
          </Form.Item>
        </Col>

        {showEngineFields && (
          <>
            <Col xs={24} md={12}>
              <Form.Item {...vehicleFuelProps} rules={isMini ? [] : [required]}>
                <VehicleFuelSelect disabled={isEditable} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                {...vehicleTransmissionProps}
                rules={isMini ? [] : [required]}
              >
                <VehicleTransmissionSelect disabled={isEditable} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                {...vehicleMileAgeProps}
                rules={isMini ? [] : [required]}
              >
                <InputParsedNumber
                  min={0}
                  placeholder={vehicleMileAgeProps.label}
                  disabled={isEditable}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item {...vehicleSeatsProps}>
                <InputParsedNumber
                  min={0}
                  placeholder={vehicleSeatsProps.label}
                  disabled={isEditable}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item {...vehicleEngineNumberProps}>
                <Input
                  placeholder={vehicleEngineNumberProps.label}
                  disabled={isEditable}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item {...vehicleEngineCapacityProps}>
                <InputParsedNumber
                  min={0}
                  placeholder={vehicleEngineCapacityProps.label}
                  suffix={t('cm3')}
                  className="w-full"
                  disabled={isEditable}
                />
              </Form.Item>
            </Col>
          </>
        )}

        <Col xs={24} md={12}>
          <Form.Item {...vehicleWeightProps}>
            <InputParsedNumber
              min={0}
              placeholder={vehicleWeightProps.label}
              suffix={t('kg')}
              className="w-full"
              disabled={isEditable}
            />
          </Form.Item>
        </Col>

        {insurancePackage !== CascoPackage.MINI && (
          <>
            <Col xs={24} md={12}>
              <Form.Item {...vehicleIsPremium}>
                <BooleanRadioCardGroup disabled={isEditable} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item {...vehicleIsFromChina}>
                <BooleanRadioCardGroup disabled={isEditable} />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
    </Flex>
  )
}
